import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import get from 'lodash/get';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CalendarIcon from '@mui/icons-material/CalendarTodayOutlined';
import CashIcon from '@mui/icons-material/AttachMoneyOutlined';
import MuiDrawer from '@mui/material/Drawer';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import HistoryIcon from '@mui/icons-material/History';
import { HomeOutlined } from "@mui/icons-material";
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import MapPinIcon from '@mui/icons-material/LocationOnOutlined';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import { styled } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import SensorDoorIcon from '@mui/icons-material/SensorDoorOutlined';
import WorkIcon from '@mui/icons-material/WorkOutlineOutlined';

import { KeyboardTabIcon, BarChartIcon, ArrowForwardIcon, DescriptionBlueIcon, BluePersonIcon, EmailIcon, LiveHelpIcon, CalendarBlueIcon } from '../../styles/icons';
import config from '../../config/config';
import { SIDEBAR_WIDTH, SIDEBAR_CLOSED_WIDTH } from '../../config/constants';
import { formatPatientName } from '../../helpers/patients';
import { isAdmin, isBetaUser } from '../../helpers/providers';
import './Navbar.scss';
import { Icon } from '@mui/material';
import SelectPatientForm from '../scheduling/SelectPatientForm';
import BetaTag from './BetaTag';

const openedMixin = (theme) => ({
  width: SIDEBAR_WIDTH,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: SIDEBAR_CLOSED_WIDTH,
  [theme.breakpoints.up('sm')]: {
    width: SIDEBAR_CLOSED_WIDTH,
  },
});

const DrawerDesktop = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: SIDEBAR_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

const handleDrawerToggle = ({ setIsOpen }) => open => e => {
  if (e.type === 'keydown' && (e.key === 'Tab' || e.key === 'Shift')) {
    return;
  }
  setIsOpen(open);
};

const providerMenuItems = ({ navigate, providerId, onSearch }) => ([
  {
    title: 'Search',
    Icon: SearchIcon,
    disabled: false,
    onClick: () => onSearch(),
  },
  {
    title: 'Dashboard',
    Icon: BarChartIcon,
    disabled: false,
    onClick: () => navigate('/providers'),
  },
  {
    title: 'Sessions',
    disabled: false,
    Icon: DescriptionBlueIcon,
    onClick: () => navigate('/providers/sessions'),
  },
  {
    title: 'Patients',
    disabled: false,
    Icon: BluePersonIcon,
    onClick: () => navigate('/providers/patients'),
  },
  {
    title: 'Leads',
    Icon: SensorDoorIcon,
    onClick: () => navigate(`/providers/leads/list`),
  },
  {
    title: 'Messages',
    Icon: EmailIcon,
    onClick: () => navigate('/providers/messages'),
  },
  {
    title: 'Cases',
    Icon: WorkIcon,
    onClick: () => navigate(`/providers/cases`),
  },
  {
    title: 'Provider Calendar',
    Icon: CalendarIcon,
    onClick: () => navigate(`/providers/calendar`),
  },
  {
    title: 'Session Types',
    Icon: CashIcon,
    onClick: () => navigate(`/providers/${providerId}/session-types/list`),
  },
  {
    title: 'Service Types',
    Icon: CashIcon,
    onClick: () => navigate(`/providers/service-types/list`),
  },
  {
    title: 'Availability Zones',
    Icon: MapPinIcon,
    onClick: () => navigate(`/providers/availabilities/list`),
  },
  {
    title: 'Admin',
    disabled: false,
    adminOnly: true,
    Icon: ElectricBoltIcon,
    onClick: () => navigate('/admin'),
  },
]);

const patientMenuItems = ({ logout, navigate, user }) => ([
  {
    title: 'Dashboard',
    Icon: BarChartIcon,
    disabled: false,
    onClick: () => navigate('/clients'),
  },
  {
    title: 'Sessions',
    disabled: false,
    Icon: DescriptionBlueIcon,
    onClick: () => navigate('/clients/sessions'),
  },
  {
    title: 'Messages',
    Icon: EmailIcon,
    onClick: () => navigate('/clients/messages'),
  },
  // {
  //   title: 'The MX Way',
  //   disabled: true,
  //   Icon: LiveHelpIcon,
  //   onClick: () => navigate('/'),
  // },
  {
    title: 'Scheduling',
    Icon: CalendarBlueIcon,
    onClick: () => navigate(`/clients/browse-providers`),
  },
  {
    title: 'Medical History',
    Icon: HistoryIcon,
    onClick: () => navigate(`/clients/view/${user.patient.id}?excludeAccountInfo=true`),
  },
]);

const MenuLeft = props => {
  const { logout, isOpen, setIsOpen, isMobile, onSearch } = props;
  const Drawer = isMobile ? MuiDrawer : DrawerDesktop;
  const navigate = useNavigate();

  const onToggleDrawer = handleDrawerToggle({ setIsOpen });
  const menuItems = (
    get(props, 'user.is_provider')
      ? providerMenuItems({ 
        logout, navigate, 
        providerId: props.user?.provider?.id,
        onSearch,
      })
        .filter(m => !m?.adminOnly || isAdmin(props?.user))
        .filter(m => !m?.betaOnly || isBetaUser(props?.user))
        : patientMenuItems({ logout, navigate, user: props.user })
  );

  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [isMobile, setIsOpen]);

  return (
    <Box className={`Drawer-menu-wrapper ${!isOpen ? 'Drawer-menu-wrapper-closed' : ''}`}>
      <Drawer variant={!isMobile ? 'permanent' : ''} onClose={() => setIsOpen(false)} open={isOpen} style={{ borderRight: 0 }}>
        <Box className="Drawer-menu">
          <Box className="Drawer-menu__head">
            {
              !isOpen
                ? <IconButton className="Drawer-menu__head__btn-menu-icon" onClick={onToggleDrawer(true)}>
                  <MenuIcon />
                </IconButton>
                : <Stack direction='row' justifyContent='space-between' padding={1}>
                  <Stack justifyContent='center'>
                    <Avatar
                      variant="square"
                      sx={{
                        width: 140,
                        height: 31,
                      }}
                      src="/images/logo-text.png"
                      alt="Logo"
                      onClick={() => navigate('/')}
                    />
                  </Stack>
                  <IconButton
                    onClick={onToggleDrawer(false)}
                    className="Drawer-menu__head__btn-arrow-forward-icon"
                  >
                    <ArrowForwardIcon />
                  </IconButton>
                </Stack>
            }
          </Box>
          <List className="Drawer-menu__list">
            {menuItems.map((item, i) => (
              <Box key={'item-' + i}>
                <ListItem key={'item-ListItem' + i} disablePadding>
                  <ListItemButton onClick={item.onClick} disabled={item.disabled}>
                    <ListItemIcon>
                      <item.Icon style={{ color: config().colors.primary }} />
                    </ListItemIcon>
                    <Typography variant='paragraphMedium500' letterSpacing='0.01rem' color='text.primary'>
                      {item.title}
                    </Typography>
                    {
                      item.betaOnly && <>
                        <BetaTag />
                      </>
                    }
                  </ListItemButton>
                </ListItem>
                {item.appendDivider ? <Divider /> : null}
              </Box>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

const UserDropdown = props => {
  const { logout, user } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const handleClose = () => setAnchorEl(null);
  const settingLink = user.is_provider ? '/providers/settings' : '/clients/settings';

  return (
    <Box className='UserDropdown'>
      <Button
        id='user-dropdown-button'
        aria-controls={!!anchorEl ? 'user-dropdown' : undefined}
        aria-haspopup='true'
        aria-expanded={!!anchorEl ? 'true' : undefined}
        onClick={e => setAnchorEl(e.currentTarget)}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <Avatar
            className='UserDropdown__avatar'
            src={user.imgSrc || user.public_profile_img_url || user?.provider?.public_profile_img_url}
            alt={formatPatientName({ user })}
          />
          <Typography
            variant='h2'
            color='text.black'
            lineHeight='1.40625rem'
            sx={{
              marginBottom: '4px !important'
            }}
          >
            Hi,
            <Typography
              variant='h2'
              color='text.blue'
              lineHeight='1.40625rem'
              component="span">
              {' '}{formatPatientName({ user })}
            </Typography>
          </Typography>
        </Stack>
      </Button>
      <Menu
        className='DropdownMenu'
        id='user-dropdown'
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleClose}
        disableScrollLock={true}
        MenuListProps={{
          'aria-labelledby': 'user-dropdown-button',
        }}
      >
        <MenuItem onClick={() => navigate(settingLink)}>
          <ListItemIcon>
            <BarChartIcon />
          </ListItemIcon>
          <Typography variant='normal500' color='text.primary'>
            Settings
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => logout()}>
          <ListItemIcon>
            <KeyboardTabIcon />
          </ListItemIcon>
          <Typography variant='normal500' color='text.primary'>
            Log Out
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

const Navbar = props => {
  const { isOpen, setIsOpen, isMobile, leftDistance, user, title, showUser } = props;
  const [searchDialogIsOpen, setSearchDialogIsOpen] = useState(false);
  const navigate = useNavigate();
  return (
    <>
      <Dialog
        fullWidth={true}
        maxWidth='sm'
        open={searchDialogIsOpen}
        onClose={() => setSearchDialogIsOpen(false)}
      >
        <SelectPatientForm title='Find a Patient' />
      </Dialog>
      <Box className="NavbarComponent" style={{ left: leftDistance }}>
        <Box className="Navbar__top">
          <Stack direction='row' alignItems='center' justifyContent='space-between' className="Navbar__top__inner">
            {isMobile
              ?
              <>
                {
                  showUser
                    ? user && <UserDropdown {...props} isMobile={isMobile} />
                    : title &&
                    <>
                      <HomeOutlined
                        className="Navbar__top__home-icon"
                        fill='var(--light-text) !important'
                        sx={{ fontSize: '30px', '&:hover': { cursor: 'pointer' } }}
                        onClick={() => {
                          navigate('/')
                        }}
                      />
                      <Stack
                        direction='row'
                        justifyContent='center'
                        className='Navbar__top__title-box w-100'
                      >
                        <Typography variant='pageTitle' noWrap>
                          {title}
                        </Typography>
                      </Stack>
                    </>

                }
                <Box>
                  <IconButton className='Navbar__top__menu-icon p-0' onClick={() => setIsOpen(!isOpen)}>
                    <MenuIcon />
                  </IconButton>
                </Box>
              </>
              :
              <>
                <Box>
                  {
                    title &&
                    <Stack direction='row' alignItems='center' justifyContent='center' className='Navbar__top__title-box'>
                      <Typography variant='pageTitle' noWrap>
                        {title}
                      </Typography>
                    </Stack>
                  }
                </Box>
                <Box> 
                  {user 
                    ? <UserDropdown {...props} isMobile={isMobile} />
                    : <Button onClick={() => props.logout()}>Sign Out</Button>
                  }
                </Box>
              </>
            }
          </Stack>
        </Box>
        <MenuLeft {...props} isOpen={isOpen} onSearch={() => setSearchDialogIsOpen(true)}/>
      </Box>
    </>
  );
};

Navbar.defaultProps = {
  hideBack: false,
};

Navbar.propTypes = {
  logout: PropTypes.func.isRequired,
  reset: PropTypes.func,
  sidebarWidth: PropTypes.number,
  sidebarClosedWidth: PropTypes.number,
};

export default Navbar;
